"use client"

import { ReactNode, useCallback, useState } from "react"
import { Alert, Linking, Pressable } from "react-native"
import * as WebBrowser from "expo-web-browser"
import { styled } from "nativewind"
import { FormattedMessage, useIntl } from "react-intl"

import {
  Box,
  Button,
  COMMUNITY_GUIDELINES_URL,
  HELP_CENTER_URL,
  Icon,
  isWeb,
  LOGOUT_URI,
  NOTIFICATION_SETTINGS_URI,
  P,
  PRIVACY_POLICY_URI,
  TERMS_OF_SERVICE_URI,
  useAppConfig,
  User,
} from "@bullseye/components"
import { useRouter } from "@bullseye/navigation"

import { DeleteProfileModal } from "./DeleteProfileModal"

const rowStyles = "flex w-full cursor-pointer flex-row justify-between py-4"

type LinkRowProps = {
  presentation: "sheet" | "external" | "internal"
  href: string
  label: ReactNode
}

function LinkRow({ label, href, presentation }: LinkRowProps) {
  const router = useRouter()

  const onPress = () => {
    if (presentation === "internal") {
      router.push(href)
      return
    }

    if (presentation === "sheet") {
      return WebBrowser.openBrowserAsync(href)
    }

    return Linking.openURL(href)
  }

  const props = isWeb()
    ? {
        href,
      }
    : {
        onPress,
      }

  return (
    <Row className={rowStyles} role={isWeb() ? "link" : "button"} {...props}>
      <P>{label}</P>
      <Icon type="chevron-right" />
    </Row>
  )
}

const Row = styled(Pressable)

const Br = () => (
  <Box className="align-self-center w-full border-b border-solid border-gray-100" />
)

export const UserSettingsLinks = ({ user }: { user: User }) => {
  const [showDeletionModal, setShowDeletionModal] = useState(false)
  const { hankAppUrl } = useAppConfig()
  const router = useRouter()
  const intl = useIntl()

  const handleLogout = useCallback(() => {
    const confirmMessage = intl.formatMessage({ id: "general.logOut.confirm" })
    const signOutTitle = intl.formatMessage({ id: "general.logOut.title" })
    const cancelText = intl.formatMessage({ id: "general.cancel" })

    if (isWeb()) {
      // Web uses window.confirm
      if (window.confirm(confirmMessage)) {
        router.push(LOGOUT_URI)
      }
    } else {
      // Native uses Alert.alert
      Alert.alert(signOutTitle, confirmMessage, [
        { text: cancelText, style: "cancel" },
        { text: signOutTitle, style: "destructive", onPress: () => router.push(LOGOUT_URI) },
      ])
    }
  }, [router, intl])
  return (
    <Box className="flex w-full flex-1 bg-gray-10 g-2 md:bg-white md:px-4 md:g-6">
      <Box className="flex w-full overflow-hidden bg-white px-4 md:rounded-lg md:border md:border-solid md:border-gray-100 md:px-6 md:py-4">
        <LinkRow
          href={NOTIFICATION_SETTINGS_URI}
          label={
            <FormattedMessage id="navigation.account.notification-settings" />
          }
          presentation="internal"
        />
      </Box>
      <Box className="flex w-full overflow-hidden bg-white px-4 md:rounded-lg md:border md:border-solid md:border-gray-100 md:px-6 md:py-4">
        <LinkRow
          href={HELP_CENTER_URL}
          label={<FormattedMessage id="navigation.help-center" />}
          presentation="sheet"
        />
        <Br />
        <LinkRow
          href={
            isWeb() ? PRIVACY_POLICY_URI : `${hankAppUrl}${PRIVACY_POLICY_URI}`
          }
          label={<FormattedMessage id="navigation.privacyPolicy" />}
          presentation="sheet"
        />
        <Br />
        <LinkRow
          href={
            isWeb()
              ? COMMUNITY_GUIDELINES_URL
              : `${hankAppUrl}${COMMUNITY_GUIDELINES_URL}`
          }
          label={<FormattedMessage id="navigation.guidelines" />}
          presentation="sheet"
        />
        <Br />
        <LinkRow
          href={
            isWeb()
              ? TERMS_OF_SERVICE_URI
              : `${hankAppUrl}${TERMS_OF_SERVICE_URI}`
          }
          label={<FormattedMessage id="navigation.terms" />}
          presentation="sheet"
        />
        <Br />
        <Row
          className={rowStyles}
          onPress={() => {
            setShowDeletionModal(true)
          }}
          role="button"
          accessibilityLabel={intl.formatMessage({
            id: "profile.settings.deleteAccount",
          })}
        >
          <P className="text-red-600">
            <FormattedMessage id="profile.settings.deleteAccount" />
          </P>
        </Row>
        <Br />
        <Box className={rowStyles}>
          <Button as="button" variant="tertiary" handlePress={handleLogout}>
            <FormattedMessage id="general.logOut" />
          </Button>
        </Box>
      </Box>
      <DeleteProfileModal
        isOpen={showDeletionModal}
        dismiss={() => setShowDeletionModal(false)}
        userId={user.id}
      />
    </Box>
  )
}
